import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  Home, 
  Info, 
  Briefcase, 
  Users, 
  PhoneCall, 
  Wrench, 
  BookOpen, 
  ShieldCheck,
  ChevronDown,
  Scale,
  Rocket,
  Gavel,
  ShieldAlert
} from "lucide-react";

function Navbar() {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setServicesOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleBookConsultation = () => {
    navigate("/contact");
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleHomeClick = (e) => {
    e.preventDefault();
    setOpen(false);
    
    if (window.location.pathname !== "/") {
      navigate("/");
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleAboutClick = (e) => {
    e.preventDefault();
    setOpen(false);

    const scrollToAbout = () => {
      const aboutSection = document.getElementById("about");
      if (aboutSection) {
        const yOffset = -60; 
        const y = aboutSection.getBoundingClientRect().top + window.scrollY + yOffset;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    };

    if (window.location.pathname !== "/") {
      navigate("/");
      setTimeout(scrollToAbout, 100);
    } else {
      scrollToAbout();
    }
  };

  const navLinkStyles = `
    group relative flex items-center gap-1.5 text-[#3E2723] font-medium tracking-wide hover:text-[#6B1D2F] transition duration-300
    after:content-[''] after:absolute after:-bottom-1.5 after:left-0 
    after:w-0 after:h-[2px] after:bg-[#6B1D2F] 
    after:transition-all after:duration-300 hover:after:w-full cursor-pointer
  `;

  const services = [
    { name: "IPR Services", path: "/services/ipr", icon: ShieldAlert },
    { name: "Startup & MSME", path: "/services/startup", icon: Rocket },
    { name: "Corporate & NCLT", path: "/services/corporate", icon: Scale },
    { name: "Litigation", path: "/services/litigation", icon: Gavel },
  ];

  const IconProps = { size: 16, className: "text-[#3E2723]/60 group-hover:text-[#6B1D2F] transition-colors duration-300" };

  return (
    <>
      <nav 
        className={`fixed top-0 left-0 w-full z-[100] transition-all duration-300 border-b border-[#d6cbb8] ${
          scrolled ? "bg-[#EADFCF]/95 backdrop-blur-md shadow-md py-2" : "bg-[#EADFCF] shadow-sm py-4"
        }`}
      >
        <div className="flex justify-between items-center px-4 md:px-8 lg:px-12">
          
          <a href="/" onClick={handleHomeClick} className="text-lg md:text-xl font-bold text-[#3E2723] hover:text-[#6B1D2F] transition duration-300 tracking-tight shrink-0 whitespace-nowrap">
            S&S Law Offices
          </a>

          {/* 🔥 DESKTOP MENU */}
          <div className="hidden xl:flex gap-6 items-center">
            <a href="/" onClick={handleHomeClick} className={navLinkStyles}><Home {...IconProps} /> Home</a>
            <button onClick={handleAboutClick} className={navLinkStyles}><Info {...IconProps} /> About</button>
            
            <div className="relative group" ref={dropdownRef} onMouseEnter={() => setServicesOpen(true)} onMouseLeave={() => setServicesOpen(false)}>
              <Link to="/services" className={`${navLinkStyles} flex items-center gap-1`}>
                <Briefcase {...IconProps} /> Services <ChevronDown size={14} className={`transition-transform duration-300 ${servicesOpen ? 'rotate-180' : ''}`} />
              </Link>
              
              <div className={`absolute top-full left-0 w-64 bg-white border border-[#e5dccf] shadow-xl rounded-xl mt-2 py-3 transition-all duration-300 origin-top ${servicesOpen ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-95 -translate-y-2 pointer-events-none"}`}>
                {services.map((item, idx) => (
                  <Link key={idx} to={item.path} onClick={() => { setServicesOpen(false); window.scrollTo(0,0); }} className="flex items-center gap-3 px-5 py-3 text-sm text-[#3E2723] hover:bg-[#FDFBF7] hover:text-[#B8860B] transition-colors">
                    <item.icon size={18} className="text-[#B8860B]" />
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>

            <Link to="/team" onClick={() => window.scrollTo(0,0)} className={navLinkStyles}><Users {...IconProps} /> Team</Link>
            <Link to="/contact" onClick={() => window.scrollTo(0,0)} className={navLinkStyles}><PhoneCall {...IconProps} /> Contact</Link>
            <Link to="/tools" onClick={() => window.scrollTo(0,0)} className={navLinkStyles}><Wrench {...IconProps} /> Tools</Link>
            <Link to="/blog" onClick={() => window.scrollTo(0,0)} className={navLinkStyles}><BookOpen {...IconProps} /> Blog</Link>
            <Link to="/admin" onClick={() => window.scrollTo(0,0)} className={navLinkStyles}><ShieldCheck {...IconProps} /> Admin</Link>
          </div>

          <button onClick={handleBookConsultation} className="hidden xl:block bg-[#2D4A3E] text-white px-5 py-2.5 rounded shadow-sm hover:bg-[#1f332b] hover:shadow-md transition duration-300 font-medium shrink-0">
            Book Consultation
          </button>

          {/* 🔥 MOBILE VIEW: Quick Icons + Hamburger Menu */}
          <div className="flex items-center gap-3 sm:gap-4 xl:hidden ml-auto">
            
            {/* Quick Links */}
            <Link to="/services" onClick={() => window.scrollTo(0,0)} className="text-[#3E2723] hover:text-[#B8860B] transition-colors p-1" aria-label="Services">
              <Briefcase size={18} />
            </Link>
            <Link to="/contact" onClick={() => window.scrollTo(0,0)} className="text-[#3E2723] hover:text-[#B8860B] transition-colors p-1" aria-label="Contact Us">
              <PhoneCall size={18} />
            </Link>
            <Link to="/blog" onClick={() => window.scrollTo(0,0)} className="text-[#3E2723] hover:text-[#B8860B] transition-colors p-1" aria-label="Blog">
              <BookOpen size={18} />
            </Link>
            <Link to="/tools" onClick={() => window.scrollTo(0,0)} className="text-[#3E2723] hover:text-[#B8860B] transition-colors p-1" aria-label="Tools">
              <Wrench size={18} />
            </Link>

            {/* Visual Divider */}
            <div className="w-[1px] h-5 bg-[#d6cbb8] mx-0.5"></div>

            {/* Hamburger Toggle */}
            <button className="text-[#3E2723] focus:outline-none p-1" onClick={() => setOpen(!open)}>
              <div className="w-6 flex flex-col gap-1.5">
                <span className={`block h-0.5 w-full bg-current transition-transform ${open ? 'rotate-45 translate-y-2' : ''}`}></span>
                <span className={`block h-0.5 w-full bg-current transition-opacity ${open ? 'opacity-0' : 'opacity-100'}`}></span>
                <span className={`block h-0.5 w-full bg-current transition-transform ${open ? '-rotate-45 -translate-y-2' : ''}`}></span>
              </div>
            </button>
          </div>
        </div>

        {/* 🔥 MOBILE DROPDOWN */}
        <div className={`xl:hidden overflow-hidden transition-all duration-300 ease-in-out bg-[#EADFCF] absolute top-full left-0 w-full ${open ? "max-h-[90vh] opacity-100 shadow-xl border-t border-[#d6cbb8]" : "max-h-0 opacity-0"}`}>
          <div className="flex flex-col items-start gap-4 py-8 px-8 overflow-y-auto max-h-[80vh]">
            <a href="/" onClick={handleHomeClick} className={navLinkStyles}><Home {...IconProps} /> Home</a>
            <button onClick={handleAboutClick} className={navLinkStyles}><Info {...IconProps} /> About</button>
            
            <div className="w-full">
               <div className="flex items-center justify-between w-full pr-4">
                  <Link to="/services" onClick={() => { setOpen(false); window.scrollTo(0,0); }} className={navLinkStyles}>
                    <Briefcase {...IconProps} /> Services
                  </Link>
                  <button onClick={() => setServicesOpen(!servicesOpen)} className="p-2 text-[#3E2723] hover:bg-black/5 rounded-md transition-colors">
                    <ChevronDown size={18} className={`transition-transform duration-300 ${servicesOpen ? 'rotate-180' : ''}`} />
                  </button>
               </div>
               
               <div className={`pl-6 overflow-hidden transition-all duration-300 ${servicesOpen ? 'max-h-60 mt-4' : 'max-h-0 mt-0'}`}>
                  <div className="space-y-4">
                    {services.map((item, idx) => (
                      <Link key={idx} to={item.path} onClick={() => { setOpen(false); window.scrollTo(0,0); }} className="flex items-center gap-3 text-sm text-[#3E2723]/80 hover:text-[#B8860B] transition-colors">
                        <item.icon size={16} className="text-[#B8860B]" /> {item.name}
                      </Link>
                    ))}
                  </div>
               </div>
            </div>

            <Link to="/team" onClick={() => { setOpen(false); window.scrollTo(0,0); }} className={navLinkStyles}><Users {...IconProps} /> Team</Link>
            <Link to="/contact" onClick={() => { setOpen(false); window.scrollTo(0,0); }} className={navLinkStyles}><PhoneCall {...IconProps} /> Contact</Link>
            <Link to="/tools" onClick={() => { setOpen(false); window.scrollTo(0,0); }} className={navLinkStyles}><Wrench {...IconProps} /> Tools</Link>
            <Link to="/blog" onClick={() => { setOpen(false); window.scrollTo(0,0); }} className={navLinkStyles}><BookOpen {...IconProps} /> Blog</Link>
            <Link to="/admin" onClick={() => { setOpen(false); window.scrollTo(0,0); }} className={navLinkStyles}><ShieldCheck {...IconProps} /> Admin</Link>
            
            <button onClick={handleBookConsultation} className="mt-4 bg-[#2D4A3E] text-white px-6 py-3 rounded shadow transition duration-300 font-medium w-full text-center">
              Book Consultation
            </button>
          </div>
        </div>
      </nav>
      <div className="h-[73px]"></div>
    </>
  );
}

export default Navbar;